import { SearchBar } from "./SearchBar";
import { SortAndSearchBar } from "./SortAndSearchBar";

import { RecruitmentFilters } from "./RecruitmentFilters";
import { FilterType } from "@/types/test-response";
import { SortOption } from "./SortAndSearchBar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, X, Palette } from "lucide-react";
import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface UnifiedToolbarProps {
  searchQuery: string;
  onSearch: (query: string) => void;
  sortOption: SortOption;
  onSortChange: (option: SortOption) => void;
  activeFilters: FilterType[];
  onFilterChange: (filters: FilterType[]) => void;
  statusColorsEnabled?: boolean;
  onStatusColorsChange?: (enabled: boolean) => void;
}

export function UnifiedToolbar({
  searchQuery,
  onSearch,
  sortOption,
  onSortChange,
  activeFilters,
  onFilterChange,
  statusColorsEnabled = false,
  onStatusColorsChange,
}: UnifiedToolbarProps) {
  const [filtersOpen, setFiltersOpen] = useState(activeFilters.length > 0);

  const hasActiveFilters = activeFilters.length > 0;

  const clearAllFilters = () => {
    onFilterChange([]);
    setFiltersOpen(false);
  };

  return (
    <div className="space-y-4">
      {/* Main toolbar */}
      <div className="flex items-center gap-4 p-4 bg-muted/30 rounded-lg border">
        {/* Left section - Search only */}
        <div className="flex-1 min-w-0">
          <SearchBar
            searchQuery={searchQuery}
            onSearch={onSearch}
          />
        </div>

        {/* Right section - All controls */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Status colors toggle */}
          {onStatusColorsChange && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md border bg-background">
              <Checkbox
                id="status-colors"
                checked={statusColorsEnabled}
                onCheckedChange={onStatusColorsChange}
              />
              <label 
                htmlFor="status-colors" 
                className="text-sm font-medium cursor-pointer flex items-center gap-1"
              >
                <Palette className="h-3 w-3" />
                Status Colors
              </label>
            </div>
          )}
          
          {/* Filter toggle with badge */}
          <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
            <CollapsibleTrigger asChild>
              <Button 
                variant="outline" 
                className="relative min-w-[100px] h-10"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="absolute -top-2 -right-2 h-5 w-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                    {activeFilters.length}
                  </span>
                )}
              </Button>
            </CollapsibleTrigger>
          </Collapsible>

          {/* Clear filters button (only show when filters are active) */}
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={clearAllFilters}
              className="text-muted-foreground hover:text-destructive min-w-[80px] h-10"
            >
              <X className="h-4 w-4 mr-1" />
              Clear all
            </Button>
          )}


          {/* Sort dropdown - moved to rightmost */}
          <div className="min-w-[180px]">
            <SortAndSearchBar
              sortOption={sortOption}
              onSortChange={onSortChange}
            />
          </div>
        </div>
      </div>

      {/* Expandable filters section */}
      <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
        <CollapsibleContent className="space-y-0">
          <div className="px-4 py-3 bg-background rounded-lg border border-border/60">
            <RecruitmentFilters
              activeFilters={activeFilters}
              onFilterChange={onFilterChange}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}